import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Circle, Triangle, X, Star } from "lucide-react";
import { Badge } from "@tabipla/ui";
import type {
  ScheduleResponseDto,
  ScheduleAvailability,
  ScheduleUserResponseDto,
} from "@tabipla/shared/types";

interface ScheduleMatrixProps {
  schedule: ScheduleResponseDto;
  localResponses: Record<string, ScheduleAvailability>;
  onResponseChange: (date: string, status: ScheduleAvailability) => void;
}

export function ScheduleMatrix({
  schedule,
  localResponses,
  onResponseChange,
}: ScheduleMatrixProps) {
  const { t } = useTranslation();

  // Find the best date index (highest available count)
  const bestDateIndex = useMemo(() => {
    if (schedule.dateSummaries.length === 0) return -1;
    let bestIdx = 0;
    let maxScore = -1;

    schedule.dateSummaries.forEach((summary, idx) => {
      // Score: available * 2 + maybe * 1
      const score = summary.available * 2 + summary.maybe;
      if (score > maxScore) {
        maxScore = score;
        bestIdx = idx;
      }
    });

    // Only return best if there are any responses
    return maxScore > 0 ? bestIdx : -1;
  }, [schedule.dateSummaries]);

  // Build user list (including current user if they have responses)
  const allUsers = useMemo(() => {
    const users: ScheduleUserResponseDto[] = [...schedule.userResponses];

    // Check if current user is already in the list
    const hasCurrentUserResponses = Object.keys(localResponses).length > 0;
    const currentUserInList = users.some(
      (u) => JSON.stringify(u.responses) === JSON.stringify(localResponses)
    );

    // If current user has local changes and not in list, show them at the top
    if (hasCurrentUserResponses && !currentUserInList && !schedule.isClosed) {
      // This will be shown as "あなた" (You)
    }

    return users;
  }, [schedule.userResponses, localResponses, schedule.isClosed]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-sm">
        <thead>
          <tr className="border-b bg-muted/30">
            <th className="sticky left-0 z-10 bg-muted/30 px-3 py-2 text-left font-medium">
              {t("schedules.respondents")}
            </th>
            {schedule.dateOptions.map((date, idx) => (
              <th
                key={date}
                className={`min-w-[80px] px-2 py-2 text-center font-medium ${
                  bestDateIndex === idx ? "bg-green-100 dark:bg-green-950/30" : ""
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <span>{formatDateShort(date)}</span>
                  {bestDateIndex === idx && (
                    <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Current user row (if not closed and has local responses) */}
          {!schedule.isClosed && (
            <tr className="border-b bg-primary/5">
              <td className="sticky left-0 z-10 bg-primary/5 px-3 py-2">
                <Badge variant="default" className="text-xs">
                  {t("expenses.you")}
                </Badge>
              </td>
              {schedule.dateOptions.map((date, idx) => (
                <td
                  key={date}
                  className={`px-2 py-2 text-center ${
                    bestDateIndex === idx ? "bg-green-50 dark:bg-green-950/20" : ""
                  }`}
                >
                  <div className="flex justify-center gap-1">
                    <MatrixResponseButton
                      status="available"
                      isSelected={localResponses[date] === "available"}
                      onClick={() => onResponseChange(date, "available")}
                    />
                    <MatrixResponseButton
                      status="maybe"
                      isSelected={localResponses[date] === "maybe"}
                      onClick={() => onResponseChange(date, "maybe")}
                    />
                    <MatrixResponseButton
                      status="unavailable"
                      isSelected={localResponses[date] === "unavailable"}
                      onClick={() => onResponseChange(date, "unavailable")}
                    />
                  </div>
                </td>
              ))}
            </tr>
          )}

          {/* Other users' responses */}
          {allUsers.map((user) => (
            <tr key={user.userId} className="border-b last:border-0">
              <td className="sticky left-0 z-10 bg-background px-3 py-2">
                <span className="font-medium">{user.userName}</span>
              </td>
              {schedule.dateOptions.map((date, idx) => {
                const status = user.responses[date];
                return (
                  <td
                    key={date}
                    className={`px-2 py-2 text-center ${
                      bestDateIndex === idx ? "bg-green-50 dark:bg-green-950/20" : ""
                    }`}
                  >
                    {status && <AvailabilityIcon status={status} className="mx-auto h-5 w-5" />}
                  </td>
                );
              })}
            </tr>
          ))}

          {/* Summary row */}
          <tr className="border-t-2 bg-muted/50 font-medium">
            <td className="sticky left-0 z-10 bg-muted/50 px-3 py-2">{t("schedules.summary")}</td>
            {schedule.dateSummaries.map((summary, idx) => (
              <td
                key={summary.date}
                className={`px-2 py-2 text-center ${
                  bestDateIndex === idx ? "bg-green-100 dark:bg-green-950/30" : ""
                }`}
              >
                <div className="flex items-center justify-center gap-1 text-xs">
                  <span className="text-green-600">{summary.available}</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-yellow-600">{summary.maybe}</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-red-600">{summary.unavailable}</span>
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function AvailabilityIcon({
  status,
  className,
}: {
  status: ScheduleAvailability;
  className?: string;
}) {
  switch (status) {
    case "available":
      return <Circle className={`fill-green-500 text-green-500 ${className}`} />;
    case "maybe":
      return <Triangle className={`fill-yellow-500 text-yellow-500 ${className}`} />;
    case "unavailable":
      return <X className={`text-red-500 ${className}`} />;
  }
}

function MatrixResponseButton({
  status,
  isSelected,
  onClick,
}: {
  status: ScheduleAvailability;
  isSelected: boolean;
  onClick: () => void;
}) {
  const baseClass =
    "flex h-6 w-6 items-center justify-center rounded-md border transition-all hover:scale-110 active:scale-95";
  const selectedClass = {
    available: "border-green-500 bg-green-100 dark:bg-green-950/50",
    maybe: "border-yellow-500 bg-yellow-100 dark:bg-yellow-950/50",
    unavailable: "border-red-500 bg-red-100 dark:bg-red-950/50",
  };
  const unselectedClass = "border-transparent bg-muted/50 opacity-40 hover:opacity-100";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClass} ${isSelected ? selectedClass[status] : unselectedClass}`}
    >
      <AvailabilityIcon status={status} className="h-3.5 w-3.5" />
    </button>
  );
}

function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
  const weekday = weekdays[date.getDay()];
  return `${month}/${day}(${weekday})`;
}
